import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import Configuration from '../../config/einvoicing-configuration';
import { AuthenticationResult } from '@azure/msal-browser';
import { Loading } from '../../components/common/Loading';
import { DatePicker, defaultDatePickerStrings } from '@fluentui/react-datepicker-compat';
import { 
    Dropdown, 
    Option, 
    Button, 
    Switch, 
    Label, 
    Input,
    makeStyles,
    shorthands,
    tokens,
    MessageBar,
    MessageBarBody
} from '@fluentui/react-components';
import { ErrorState } from '../../components/common/ErrorState';
import { getOptionsFromDimension } from "../../pages/dashboard/DocumentSearchFilterOptions";
import { ChartTitleFriendlyNames, CountryCodeToFriendlyNameMapping, ModeToFriendlyNameMapping, SupplierIdToFriendlyNameMapping } from "../../utils/FriendlyNameMappings";
import { FilterKey } from '../dashboard/DocumentSearchFilters';
import { constructDocumentSearchUrl, constructLatencyUrl, constructPostRequest, getDefaultLatencyParams } from '../../controller/dashboardController';
import { SearchParams } from './SearchParams';
import { CountrySupplierLatencies } from './CountrySupplierLatencies';
import { LatencyPoint } from './LatencyPoint';
import { SLAPoint } from './SLAPoint';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import { usePageTitle } from '../../hooks/usePageTitle';
import './latency.css'

// Local interface for Chart Props
interface IChartProps {
    chartTitle: string;
    lineChartData: {
        legend: string;
        data: { x: Date, y: number, xAxisCalloutData?: string }[]
    }[];
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalL),
    },
    filterContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap(tokens.spacingHorizontalL),
        marginBottom: tokens.spacingVerticalL,
    },
    dateTimeCombo: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalS),
        minWidth: '200px',
    },
    dropdownContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap(tokens.spacingHorizontalM),
        marginTop: tokens.spacingVerticalM,
    },
    dropdown: {
        minWidth: '200px',
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    chartContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap(tokens.spacingHorizontalL),
        marginTop: tokens.spacingVerticalM,
    },
    chart: {
        flexGrow: 1,
        minWidth: '400px',
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
        ...shorthands.padding(tokens.spacingVerticalM),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
    }
});

const SimpleLineChart = ({ data, height = 300 }: { data: IChartProps, height?: number }) => {
    const [width, setWidth] = useState(500);
    const containerRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current) {
            setWidth(containerRef.current.clientWidth);
        }
        const handleResize = () => {
            if (containerRef.current) {
                setWidth(containerRef.current.clientWidth);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const allPoints = data.lineChartData.flatMap(d => d.data);
    if (allPoints.length === 0) return <div>No data</div>;

    const xValues = allPoints.map(p => p.x.getTime());
    const yValues = allPoints.map(p => p.y);

    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = 0; // Always start Y from 0 for better visualization of magnitude
    const maxY = Math.max(...yValues) * 1.1; // Add 10% padding on top

    const padding = { top: 20, right: 20, bottom: 30, left: 40 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const getX = (val: number) => padding.left + ((val - minX) / (maxX - minX || 1)) * chartWidth;
    const getY = (val: number) => height - padding.bottom - ((val - minY) / (maxY - minY || 1)) * chartHeight;

    const colors = ['#0078d4', '#107c10', '#d83b01', '#a80000', '#5c2d91'];
    const dashPatterns = ['', '6,3', '2,2', '8,4,2,4', '4,4'];

    return (
        <div ref={containerRef} style={{ width: '100%' }}>
            <div style={{ marginBottom: 10, fontWeight: 600, textAlign: 'center' }}>{data.chartTitle}</div>
            <svg width={width} height={height} style={{ overflow: 'visible' }}>
                {/* Y Axis */}
                <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke={tokens.colorNeutralStroke1} />
                {/* X Axis */}
                <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke={tokens.colorNeutralStroke1} />

                {/* Y Axis Labels (approximate) */}
                {[0, 0.5, 1].map(t => {
                    const val = minY + t * (maxY - minY);
                    const y = getY(val);
                    return (
                        <g key={t}>
                            <line x1={padding.left - 5} y1={y} x2={padding.left} y2={y} stroke={tokens.colorNeutralStroke1} />
                            <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="10" fill={tokens.colorNeutralForeground1}>
                                {val.toFixed(2)}
                            </text>
                        </g>
                    );
                })}

                {/* Lines */}
                {data.lineChartData.map((line, index) => {
                    const points = line.data.map(p => `${getX(p.x.getTime())},${getY(p.y)}`).join(' ');
                    return (
                        <polyline 
                            key={index} 
                            points={points} 
                            fill="none" 
                            stroke={colors[index % colors.length]} 
                            strokeWidth="2"
                            strokeDasharray={dashPatterns[index % dashPatterns.length] || undefined}
                        />
                    );
                })}
            </svg>
            {/* Legend */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 10 }}>
                {data.lineChartData.map((line, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: '#242424' }}>
                        <svg width="16" height="4" style={{ marginRight: 5 }}>
                            <line x1="0" y1="2" x2="16" y2="2" stroke={colors[index % colors.length]} strokeWidth="2" strokeDasharray={dashPatterns[index % dashPatterns.length] || undefined} />
                        </svg>
                        <span>{line.legend}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

interface LatencyProps { }

function Latency(props: LatencyProps) {
usePageTitle('Latencies and SLA');
const styles = useStyles();
    const { instance, accounts } = useMsal();
    const [accessToken, setAccessToken] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [searchParams, setSearchParams] = useState(getDefaultLatencyParams());
    const [facets, setFacets] = useState<any[]>([]);
    const [latency, setLatency] = useState<CountrySupplierLatencies[]>([]);
    const [useUTC, setUseUTC] = useState(true);
    const [dateValidationError, setDateValidationError] = useState<string | null>(null);

    const onDropdownChange = (event: any, data: any, filterKey: FilterKey, index: number): void => {
        if (data.optionValue) {
            let filterVal = `${data.optionValue}`;
            setSearchParams({
                ...searchParams,
                filters: {
                    ...searchParams.filters,
                    [filterKey]: [filterVal]
                }
            })
        }
    };

    async function getAccessToken() {
        if (accessToken === '') {
            const request = {
                ...Configuration.msalTokenRequest,
                account: accounts[0]
            };
            const result: AuthenticationResult = await instance.acquireTokenSilent(request);
            setAccessToken(result.idToken);
            return result.idToken
        }
        return accessToken;
    }

    function mapLatency(resultJson: any[]): CountrySupplierLatencies[] {
        const latencies: CountrySupplierLatencies[] = []
        resultJson.forEach(row => {
            let existingLatencyIndex = latencies.findIndex(item => item.countryCode === CountryCodeToFriendlyNameMapping[row.countryCode] && item.supplier === SupplierIdToFriendlyNameMapping[row.supplier] && item.mode === ModeToFriendlyNameMapping[row.mode])
            if (existingLatencyIndex === -1) {
                const latency: CountrySupplierLatencies = {
                    countryCode: CountryCodeToFriendlyNameMapping[row.countryCode],
                    eInvLatencies: [],
                    partnerLatencies: [],
                    slaPercentages: [],
                    supplierLatencies: [],
                    supplier: SupplierIdToFriendlyNameMapping[row.supplier],
                    mode: ModeToFriendlyNameMapping[row.mode]
                }
                existingLatencyIndex = latencies.push(latency) - 1
            }
            latencies[existingLatencyIndex].slaPercentages.push({
                slaPercentage: row.slaPercentage,
                timeStamp: row.timeStamp
            })
            latencies[existingLatencyIndex].eInvLatencies.push({
                averageLatency: row.averageLatency / 1000,
                maxLatency: row.maxLatency / 1000,
                minLatency: row.minLatency / 1000,
                _95thPercentileLatency: row.percentile95Latency / 1000,
                _99thPercentileLatency: row.percentile99Latency / 1000,
                timeStamp: row.timeStamp
            })

            latencies[existingLatencyIndex].partnerLatencies.push({
                averageLatency: row.averagePartnerLatency / 1000,
                maxLatency: row.maxPartnerLatency / 1000,
                minLatency: row.minPartnerLatency / 1000,
                _95thPercentileLatency: row.percentile95PartnerLatency / 1000,
                _99thPercentileLatency: row.percentile99PartnerLatency / 1000,
                timeStamp: row.timeStamp
            })

            latencies[existingLatencyIndex].supplierLatencies.push({
                averageLatency: row.averageSupplierLatency / 1000,
                maxLatency: row.maxSupplierLatency / 1000,
                minLatency: row.minSupplierLatency / 1000,
                _95thPercentileLatency: row.percentile95SupplierLatency / 1000,
                _99thPercentileLatency: row.percentile99SupplierLatency / 1000,
                timeStamp: row.timeStamp
            })
        });
        return latencies
    }

    function useLatency(params: SearchParams) {
        useEffect(() => {
            const latencyUrl: string = constructLatencyUrl(Configuration.eventServiceUrl, params.startTime, params.endTime);
            const searchUrl: string = constructDocumentSearchUrl(Configuration.eventServiceUrl, params.startTime, params.endTime, "*");
            async function startLatency() {
                if (!ignore) {
                    try {
                        const requestParams = constructPostRequest(params.filters, await getAccessToken());
                        const result = await fetch(latencyUrl, requestParams);
                        const facetParams = constructPostRequest(params, await getAccessToken());
                        const facetResults = await fetch(searchUrl, facetParams);
                        if (facetResults !== undefined && facetResults.ok && facets.length === 0) {
                            const facetJson = await facetResults.json()
                            setFacets(facetJson.facets)
                        }
                        if (result !== undefined && result.ok) {
                            const resultJson = await result.json()
                            setLatency(mapLatency(resultJson))
                            setIsLoading(false);
                        }
                        else {
                            setIsLoading(false);
                            setIsError(true);
                        }
                    }
                    catch (error) {
                        setIsLoading(false);
                        setIsError(true);
                    }
                }
            }

            let ignore = false;
            startLatency();
            return () => {
                ignore = true;
            }
        }, [params]);
    }

    useLatency(searchParams);

    function convertUTCDateToLocalDate(date: Date) {
        var newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
        var offset = date.getTimezoneOffset() / 60;
        var hours = date.getHours();
        newDate.setHours(hours - offset);
        return newDate;
    }

    function validateDateRange(startTime: Date, endTime: Date): string | null {
        if (startTime >= endTime) {
            return "Start date/time must be before end date/time. Please adjust the start date to be earlier than the end date, or set the end date to a later value.";
        }
        return null;
    }

    function endTimeChanged(e: React.ChangeEvent<HTMLInputElement>) {
        const timeString = e.target.value;
        if (!timeString) return;
        
        const [hours, minutes] = timeString.split(':').map(Number);
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);

        var newDate = searchParams.endTime
        if (useUTC) {
            var utcDate = new Date(newDate.getTime() + newDate.getTimezoneOffset() * 60000);
            utcDate.setHours(date.getHours())
            utcDate.setMinutes(date.getMinutes())
            newDate = convertUTCDateToLocalDate(utcDate);
        }
        else {
            newDate.setHours(date.getHours())
            newDate.setMinutes(date.getMinutes())
        }
        const error = validateDateRange(searchParams.startTime, newDate);
        setDateValidationError(error);
        setSearchParams({ ...searchParams, endTime: newDate });
    }

    function endDateChanged(date: Date | null | undefined) {
        var newDate = date
        if (newDate && newDate !== null) {
            if (useUTC) {
                newDate = convertUTCDateToLocalDate(newDate);
            }
            newDate.setHours(searchParams.endTime.getHours())
            newDate.setMinutes(searchParams.endTime.getMinutes())
            const error = validateDateRange(searchParams.startTime, newDate);
            setDateValidationError(error);
            setSearchParams({ ...searchParams, endTime: newDate });
        }
    }

    function startDateChanged(date: Date | null | undefined) {
        var newDate = date
        if (newDate && newDate !== null) {
            if (useUTC) {
                newDate = convertUTCDateToLocalDate(newDate);
            }
            newDate.setHours(searchParams.startTime.getHours())
            newDate.setMinutes(searchParams.startTime.getMinutes())
            const error = validateDateRange(newDate, searchParams.endTime);
            setDateValidationError(error);
            setSearchParams({ ...searchParams, startTime: newDate });
        }
    }

    function startTimeChanged(e: React.ChangeEvent<HTMLInputElement>) {
        const timeString = e.target.value;
        if (!timeString) return;
        
        const [hours, minutes] = timeString.split(':').map(Number);
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);

        var newDate = searchParams.startTime
        if (useUTC) {
            var utcDate = new Date(newDate.getTime() + newDate.getTimezoneOffset() * 60000);
            utcDate.setHours(date.getHours())
            utcDate.setMinutes(date.getMinutes())
            newDate = convertUTCDateToLocalDate(utcDate);
        }
        else {
            newDate.setHours(date.getHours())
            newDate.setMinutes(date.getMinutes())
        }
        const error = validateDateRange(newDate, searchParams.endTime);
        setDateValidationError(error);
        setSearchParams({ ...searchParams, startTime: newDate });
    }

    const buildDropDowns: string[] = [
        'eInvPartnerId',
        'countryCode',
        'supplier',
        'documentType',
        'mode'
    ];

    function getCalloutDateString(date: Date): string {
        let isoString:string = date.toISOString();
        let [dateString, timeString] = isoString.split('T');
        [timeString] = timeString.split('.');
        return `${dateString} ${timeString}`;
    }

    function getLineChartData(eInvLatencies: LatencyPoint[], chartTitle: string): IChartProps {
        return {
            chartTitle: chartTitle,
            lineChartData: [
                {
                    legend: "Average",
                    data: eInvLatencies.map((latency: LatencyPoint) => { return { x: GetDateValue(useUTC, new Date(latency.timeStamp)), y: latency.averageLatency, xAxisCalloutData: getCalloutDateString(GetDateValue(useUTC, new Date(latency.timeStamp))) } }),
                },
                {
                    legend: "Max",
                    data: eInvLatencies.map((latency: LatencyPoint) => { return { x: GetDateValue(useUTC, new Date(latency.timeStamp)), y: latency.maxLatency, xAxisCalloutData: getCalloutDateString(GetDateValue(useUTC, new Date(latency.timeStamp))) } })
                },
                {
                    legend: "Min",
                    data: eInvLatencies.map((latency: LatencyPoint) => { return { x: GetDateValue(useUTC, new Date(latency.timeStamp)), y: latency.minLatency, xAxisCalloutData: getCalloutDateString(GetDateValue(useUTC, new Date(latency.timeStamp)))} })
                },
                {
                    legend: "95th Perc.",
                    data: eInvLatencies.map((latency: LatencyPoint) => { return { x: GetDateValue(useUTC, new Date(latency.timeStamp)), y: latency._95thPercentileLatency, xAxisCalloutData: getCalloutDateString(GetDateValue(useUTC, new Date(latency.timeStamp))) } })
                },
                {
                    legend: "99th Perc.",
                    data: eInvLatencies.map((latency: LatencyPoint) => { return { x: GetDateValue(useUTC, new Date(latency.timeStamp)), y: latency._99thPercentileLatency, xAxisCalloutData: getCalloutDateString(GetDateValue(useUTC, new Date(latency.timeStamp))) } })
                },
            ]
        }
    }
    function getSLALineChartData(slaNumbers: SLAPoint[], chartTitle: string): IChartProps {
        return {
            chartTitle: chartTitle,
            lineChartData: [{
                legend: 'In SLA %',
                data: slaNumbers.map((num: SLAPoint) => { return { x: new Date(num.timeStamp), y: num.slaPercentage } })
            }]
        }
    }

    function onTimeZoneChange(event: React.ChangeEvent<HTMLInputElement>, data: any) {
        setUseUTC(data.checked ?? false)
        refresh()
    }
    function refresh() {
        setSearchParams({ ...searchParams });
    }

    function GetDateValue(useUTC: boolean, date: Date): Date {
        return useUTC ? new Date(date.getTime() + date.getTimezoneOffset() * 60000) : date;
    }

    function formatTimeForInput(date: Date): string {
        const d = GetDateValue(useUTC, date);
        return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }

    if (isLoading) {
        return <Loading label='Loading Data...' />
    }
    else if (!isError) {

        return (
            <div className={styles.container}>
                <h1>Latencies and SLA</h1>
                <div className={styles.filterContainer}>
                    {dateValidationError && (
                        <div style={{ width: '100%' }}>
                            <MessageBar intent="error" aria-live="assertive">
                                <MessageBarBody>{dateValidationError}</MessageBarBody>
                            </MessageBar>
                        </div>
                    )}
                    <div className={styles.dateTimeCombo}>
                        <Label htmlFor="latency-start-date">Start Date</Label>
                        <DatePicker
                            placeholder="Select a Start Date..."
                            aria-label="Start date"
                            strings={defaultDatePickerStrings}
                            value={GetDateValue(useUTC, searchParams.startTime)}
                            onSelectDate={startDateChanged}
                        />
                        <Label htmlFor="latency-start-time">Start Time</Label>
                        <Input
                            id="latency-start-time"
                            type="time"
                            value={formatTimeForInput(searchParams.startTime)}
                            onChange={startTimeChanged}
                            aria-label="Start time"
                            aria-description="Set the start time for the latency data range"
                            autoComplete="off"
                            aria-invalid={dateValidationError ? true : undefined}
                            aria-errormessage={dateValidationError ? "latency-date-range-error" : undefined}
                        />
                    </div>
                    <div className={styles.dateTimeCombo}>
                        <Label htmlFor="latency-end-date">End Date</Label>
                        <DatePicker
                            placeholder="Select an End Date..."
                            aria-label="End date"
                            strings={defaultDatePickerStrings}
                            value={GetDateValue(useUTC, searchParams.endTime)}
                            onSelectDate={endDateChanged}
                        />
                        <Label htmlFor="latency-end-time">End Time</Label>
                        <Input
                            id="latency-end-time"
                            type="time"
                            aria-label="End time"
                            value={formatTimeForInput(searchParams.endTime)}
                            onChange={endTimeChanged}
                            aria-description="Set the end time for the latency data range"
                            aria-invalid={dateValidationError ? true : undefined}
                            aria-errormessage={dateValidationError ? "latency-date-range-error" : undefined}
                        />
                    </div>
                    {dateValidationError && (
                        <span id="latency-date-range-error" style={{ color: '#A80000', fontSize: '12px', width: '100%' }} role="alert">
                            {dateValidationError}
                        </span>
                    )}
                </div>
                <div className={styles.dropdownContainer} tabIndex={0} role="region" aria-label="Latency filters">
                    {buildDropDowns.map(a => facets.filter(f => f.dimension === a)[0]).map((item: any, index: number) => (
                        <div key={index} className={styles.dropdown}>
                            <Label id={`latency-filter-${item.dimension}`}>{ChartTitleFriendlyNames[item.dimension]}</Label>
                            <Dropdown
                                placeholder="Select..."
                                aria-labelledby={`latency-filter-${item.dimension}`}
                                aria-haspopup="listbox"
                                onOptionSelect={(ev, data) => onDropdownChange(ev, data, item.dimension, index)}
                            >
                                {getOptionsFromDimension(item, false).map((opt: any) => (
                                    <Option key={opt.key} value={opt.key} text={opt.text}>
                                        {opt.text}
                                    </Option>
                                ))}
                            </Dropdown>
                        </div>
                    ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Switch label={useUTC ? "Time Zone: UTC" : "Time Zone: Local"} onChange={onTimeZoneChange} checked={useUTC} aria-description="Toggle between UTC and local time zone for displaying latency timestamps" />
                    <Button icon={<ArrowSyncRegular />} title="Refresh" aria-label="Refresh" aria-description="Reload the latency and SLA data" onClick={refresh}>Refresh</Button>
                </div>
                <div>
                    {latency.map((item: CountrySupplierLatencies, index: number) => (
                        <div key={index}>
                            <h3>Latencies for country/region - {item.countryCode}, Supplier - {item.supplier}, Mode - {item.mode}</h3>
                            <div className={styles.chartContainer}>
                                <div className={styles.chart}>
                                    <SimpleLineChart data={getLineChartData(item.eInvLatencies, 'EInv Latencies')} height={300} />
                                </div>
                                <div className={styles.chart}>
                                    <SimpleLineChart data={getLineChartData(item.partnerLatencies, 'Partner E2E Latencies')} height={300} />
                                </div>
                            </div>
                            <div className={styles.chartContainer}>
                                <div className={styles.chart}>
                                    <SimpleLineChart data={getLineChartData(item.supplierLatencies, 'Supplier Latencies')} height={300} />
                                </div>
                                <div className={styles.chart}>
                                    <SimpleLineChart data={getSLALineChartData(item.slaPercentages, 'SLA Percentages')} height={300} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    else {
        return (
            <ErrorState
                description="Check your internet connection, try refreshing the page or contact support."
                title="There was an issue loading the page"
            />);
    }
}

export default Latency;
